import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Folder, FolderPlus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabase';
import { useRegisterAppAssistantContext } from '@/lib/app-assistant-context';
import { type AppFolder, loadAppFolders, saveAppFolder } from '@/lib/app-collaboration';

export const FOLDER_VISIBILITY_LABELS: Record<AppFolder['visibility'], string> = {
  private: 'Juste moi',
  team: 'Équipe',
  org: 'Toute l’entreprise',
};

/** F5b: folders across apps (e.g. « Onboarding SDR »): calls, rows and reports kept together. */
export const FoldersPage = () => {
  const [folders, setFolders] = useState<AppFolder[] | null>(null);
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([]);
  const [name, setName] = useState('');
  const [teamId, setTeamId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const assistantContext = useMemo(() => ({
    surface: 'home' as const, routePath: '/folders', routeLabel: 'Dossiers',
    visibleSummary: folders ? `Dossiers d'objets d'apps (${folders.length}).` : 'Dossiers en cours de chargement.',
    visibleEntities: (folders ?? []).map((folder) => folder.name).slice(0, 12),
  }), [folders]);
  useRegisterAppAssistantContext(assistantContext);

  const reload = () => { void loadAppFolders().then(setFolders).catch((cause) => setError(cause instanceof Error ? cause.message : 'Dossiers indisponibles.')); };
  useEffect(() => {
    reload();
    void supabase.from('teams').select('id, name').is('archived_at', null).order('name')
      .then(({ data }) => setTeams((data ?? []) as Array<{ id: string; name: string }>));
  }, []);

  const create = async () => {
    if (!name.trim()) return;
    setError(null);
    try {
      await saveAppFolder({ name: name.trim(), visibility: teamId === 'org' ? 'org' : teamId ? 'team' : 'private', teamId: teamId && teamId !== 'org' ? teamId : null });
      setName('');
      reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Le dossier n’a pas pu être créé.');
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-5 py-7 sm:px-8">
      <Link to="/tools" className="text-sm text-domino-inkSoft hover:text-domino-aubergine">Applications</Link>
      <h1 className="mt-2 font-display text-[28px] font-semibold tracking-tight text-domino-ink">Dossiers</h1>
      <p className="mt-1 text-sm text-domino-inkSoft">Rangez ensemble des lignes de plusieurs applications : appels exemplaires, comptes d’un plan, rapports.</p>

      <form className="mt-6 flex flex-wrap items-center gap-2" onSubmit={(event) => { event.preventDefault(); void create(); }}>
        <input value={name} onChange={(event) => setName(event.target.value)} maxLength={80} placeholder="Nom du dossier"
          aria-label="Nom du nouveau dossier" className="min-w-0 flex-1 rounded-xl border border-domino-cardBorder px-3 py-2 text-sm sm:max-w-sm" />
        <select value={teamId} onChange={(event) => setTeamId(event.target.value)} aria-label="Partagé avec" className="rounded-xl border border-domino-cardBorder px-3 py-2 text-sm">
          <option value="">Juste moi</option>
          {teams.map((team) => <option key={team.id} value={team.id}>Équipe {team.name}</option>)}
          <option value="org">Toute l’entreprise</option>
        </select>
        <Button type="submit" className="gap-2"><FolderPlus className="h-4 w-4" />Créer</Button>
      </form>
      {error && <p role="alert" className="mt-3 text-sm text-[var(--domino-critical)]">{error}</p>}

      {folders === null ? <p role="status" className="mt-8 text-sm text-domino-inkSoft">Chargement des dossiers…</p>
        : folders.length === 0 ? <p className="mt-8 text-sm text-domino-inkSoft">Aucun dossier. Depuis la fiche d’une ligne, « Ranger dans un dossier » en crée un aussi.</p> : (
        <ul className="mt-6 rounded-2xl border border-domino-cardBorder bg-domino-white">
          {folders.map((folder) => (
            <li key={folder.id} className="border-t border-domino-cardBorder first:border-t-0">
              <Link to={`/folders/${folder.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-domino-warm">
                <Folder className="h-5 w-5 text-domino-aubergine" aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-domino-ink">{folder.name}</span>
                  {folder.description && <span className="block text-xs text-domino-inkSoft">{folder.description}</span>}
                </span>
                <span className="text-xs text-domino-inkSoft">{folder.itemCount} élément{folder.itemCount > 1 ? 's' : ''} · {FOLDER_VISIBILITY_LABELS[folder.visibility]}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
